// src/pages/Profile.js

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient, authApiClient } from '../api/apiClient';
import PageShell from '../components/PageShell';
import {
    CornerCard, PrimaryButton, GhostButton, Tag, Field, Input, TextArea, Muted,
} from '../components/ui';
import { color, font } from '../theme/tokens';

// --- 1. 动画 ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- 2. 基础布局 ---

const ProfileContainer = styled.div`
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 48px;
    align-items: start;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 32px;
    }
`;

// --- 3. 侧边栏 ---

const Sidebar = styled.aside`
    display: flex;
    flex-direction: column;
    gap: 28px;
`;

const SidebarHeader = styled(CornerCard)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const UserAvatar = styled.div`
    width: 88px;
    height: 88px;
    margin-bottom: 18px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 1px solid ${color.blood};
    outline: 1px solid ${color.line};
    outline-offset: 5px;
    background: ${color.void};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 36px;
    color: ${color.textBright};
`;

const Username = styled.h3`
    margin: 0 0 8px;
    font-family: ${font.display};
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.12em;
    color: ${color.text};
    word-break: break-all;
`;

const UserRole = styled.p`
    margin: 0;
    font-family: ${font.mono};
    font-size: 12px;
    letter-spacing: 0.2em;
    color: ${color.text3};
`;

const NavMenu = styled.nav`
    display: flex;
    flex-direction: column;
    border-top: 1px solid ${color.line};

    @media (max-width: 860px) {
        flex-direction: row;
        flex-wrap: wrap;
        border-top: none;
        border-bottom: 1px solid ${color.line};
    }
`;

const NavItem = styled.button`
    position: relative;
    display: flex;
    align-items: baseline;
    gap: 14px;
    width: 100%;
    padding: 16px 18px;
    background: ${({ $active }) => ($active ? color.surface : 'transparent')};
    border: none;
    border-bottom: 1px solid ${color.line};
    border-radius: 0;
    color: ${({ $active, $danger }) => {
        if ($danger) return color.bloodHi;
        return $active ? color.text : color.text2;
    }};
    text-align: left;
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.2em;
    cursor: pointer;
    transition: background-color 0.25s ease, color 0.25s ease;

    .num {
        font-family: ${font.mono};
        font-size: 11px;
        letter-spacing: 0.06em;
        color: ${({ $active }) => ($active ? color.bloodHi : color.text3)};
    }

    &::before {
        content: '';
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 2px;
        background: ${color.blood};
        opacity: ${({ $active }) => ($active ? 1 : 0)};
        transition: opacity 0.25s ease;
    }

    &:hover {
        background: ${color.surface2};
        color: ${({ $danger }) => ($danger ? color.bloodHi : color.text)};
    }

    @media (max-width: 860px) {
        width: auto;
        flex: 1 1 auto;
        border-bottom: none;
        padding: 12px 14px;
        font-size: 14px;
        letter-spacing: 0.12em;
        &::before { left: 0; right: 0; top: auto; bottom: 0; width: auto; height: 2px; }
    }
`;

// --- 4. 主内容区域 ---

const MainContent = styled.div`
    min-width: 0;
    color: ${color.text};
`;

const ContentWrapper = styled.div`
    animation: ${fadeIn} 0.4s ease-out;
`;

const ContentHeader = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${color.line};
`;

const ContentTitle = styled.h2`
    margin: 0;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 26px;
    letter-spacing: 0.2em;
    color: ${color.text};
`;

const ContentDescription = styled.p`
    margin: 0;
    font-size: 14px;
    letter-spacing: 0.12em;
    color: ${color.text3};
`;

// --- 5. 卡片与信息展示 ---

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    padding: 18px 0;
    border-bottom: 1px solid ${color.line};

    &:first-child { padding-top: 0; }
    &:last-child { border-bottom: none; padding-bottom: 0; }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
`;

const Label = styled.span`
    min-width: 100px;
    font-size: 13px;
    letter-spacing: 0.2em;
    color: ${color.text3};
`;

const Value = styled.span`
    flex: 1;
    min-width: 0;
    text-align: right;
    font-family: ${({ $mono }) => ($mono ? font.mono : font.body)};
    font-size: 15px;
    color: ${({ $dim }) => ($dim ? color.text2 : color.text)};
    word-break: break-word;
    ${({ $italic }) => $italic && 'font-style: italic;'}

    @media (max-width: 600px) {
        text-align: left;
        width: 100%;
    }
`;

const EditableValue = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 0;
    justify-content: flex-end;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const LevelValue = styled.span`
    font-family: ${font.mono};
    font-size: 15px;
    color: ${color.bloodHi};
    .exp { margin-left: 8px; font-size: 12px; color: ${color.text3}; }
`;

const VerifiedBadge = styled(Tag)`
    margin-left: 10px;
    vertical-align: middle;
`;

const NotVerifiedWarning = styled.div`
    margin-bottom: 24px;
    padding: 14px 18px;
    border: 1px solid rgba(224, 70, 79, 0.45);
    border-left: 2px solid ${color.bloodHi};
    background: rgba(200, 50, 62, 0.08);
    color: ${color.text};
    font-size: 14px;
    line-height: 1.7;
`;

// --- 6. 表单 ---

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 22px;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 6px;
`;

const SmallGhostButton = styled(GhostButton)`
    min-height: 34px;
    padding: 0 14px;
    font-size: 13px;
    letter-spacing: 0.2em;
    flex-shrink: 0;
`;

const DangerGhostButton = styled(GhostButton)`
    border-color: rgba(224, 70, 79, 0.6);
    color: ${color.bloodHi};
    &:hover:not(:disabled) { border-color: ${color.bloodHi}; color: ${color.textBright}; background: rgba(200, 50, 62, 0.14); }
`;

// --- 7. 消息与危险区域 ---

const Message = styled.div`
    margin-top: 20px;
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.7;
    border-left: 2px solid ${({ $error }) => ($error ? color.bloodHi : color.line2)};
    background: ${({ $error }) => ($error ? 'rgba(200, 50, 62, 0.08)' : color.surface2)};
    color: ${({ $error }) => ($error ? color.bloodHi : color.text)};
`;

const DangerZone = styled.div`
    position: relative;
    padding: 32px 36px;
    border: 1px solid rgba(224, 70, 79, 0.4);
    background: linear-gradient(180deg, rgba(200, 50, 62, 0.08), ${color.surface} 60%);

    &::before {
        content: '';
        position: absolute;
        top: -1px; left: -1px; right: -1px;
        height: 2px;
        background: ${color.blood};
    }

    @media (max-width: 640px) { padding: 22px 18px; }
`;

const DangerTitle = styled.h3`
    margin: 0 0 16px;
    font-family: ${font.display};
    font-weight: 800;
    font-size: 20px;
    letter-spacing: 0.2em;
    color: ${color.bloodHi};
`;

const DangerDescription = styled.div`
    margin-bottom: 28px;
    font-size: 15px;
    line-height: 1.9;
    color: ${color.text2};

    ul { margin: 10px 0 14px; padding-left: 0; list-style: none; }
    li { position: relative; padding-left: 20px; }
    li::before {
        content: '';
        position: absolute;
        left: 2px; top: 0.95em;
        width: 8px; height: 1px;
        background: ${color.blood};
    }
    strong { color: ${color.bloodHi}; }
`;

// --- Config ---
const menuItems = [
    { id: 'profile', label: '个人信息', num: '01' },
    { id: 'edit', label: '编辑资料', num: '02' },
    { id: 'password', label: '账户安全', num: '03' },
    { id: 'danger', label: '危险操作', num: '04', danger: true }
];

function Profile() {
    const { user, onLogout, updateUserProfile } = useAuth();
    const navigate = useNavigate();

    // 状态管理
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editNickname, setEditNickname] = useState('');
    const [editSignature, setEditSignature] = useState('');
    const [profileMessage, setProfileMessage] = useState({ text: '', error: false });

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordMessage, setPasswordMessage] = useState({ text: '', error: false });

    const [deletePassword, setDeletePassword] = useState('');
    const [deleteMessage, setDeleteMessage] = useState({ text: '', error: false });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // --- Handlers (保持逻辑不变) ---
    const handleEditProfile = () => {
        setEditNickname(user.nickname || '');
        setEditSignature(user.signature || '');
        setIsEditingProfile(true);
        setProfileMessage({ text: '', error: false });
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setProfileMessage({ text: '', error: false });
        try {
            const response = await apiClient.put('/user/profile', {
                nickname: editNickname,
                signature: editSignature
            });
            updateUserProfile(response.data.user);
            setProfileMessage({ text: response.data.message, error: false });
            setIsEditingProfile(false);
        } catch (err) {
            const message = err.response?.data?.message || '更新失败。';
            setProfileMessage({ text: message, error: true });
        }
    };

    const handleCancelEdit = () => {
        setIsEditingProfile(false);
        setEditNickname('');
        setEditSignature('');
        setProfileMessage({ text: '', error: false });
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordMessage({ text: '', error: false });
        if (newPassword !== confirmNewPassword) {
            setPasswordMessage({ text: '新密码两次输入不一致。', error: true });
            return;
        }
        try {
            const response = await authApiClient.post('/auth/change-password', {
                currentPassword,
                newPassword
            });
            setPasswordMessage({ text: response.data.message, error: false });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            const message = err.response?.data?.message || '操作失败。';
            setPasswordMessage({ text: message, error: true });
        }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        setDeleteMessage({ text: '', error: false });
        try {
            await authApiClient.post('/auth/delete-account', { password: deletePassword });
            alert('账户已成功删除。');
            onLogout();
            navigate('/');
        } catch (err) {
            const message = err.response?.data?.message || '操作失败。';
            setDeleteMessage({ text: message, error: true });
        }
    };

    // --- Render Logic ---
    const renderContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <>
                        <ContentHeader>
                            <ContentTitle>個人情報</ContentTitle>
                            <ContentDescription>个人信息 · 只读</ContentDescription>
                        </ContentHeader>

                        {!user.is_verified && (
                            <NotVerifiedWarning role="alert">
                                您的邮箱未验证。部分功能受限。请检查注册邮箱。
                            </NotVerifiedWarning>
                        )}

                        <CornerCard>
                            <InfoRow>
                                <Label>用户名</Label>
                                <Value>{user.username}</Value>
                            </InfoRow>
                            <InfoRow>
                                <Label>昵称</Label>
                                <Value $dim={!user.nickname}>{user.nickname || '未设置'}</Value>
                            </InfoRow>
                            <InfoRow>
                                <Label>邮箱</Label>
                                <Value>
                                    {user.email}
                                    {user.is_verified && <VerifiedBadge>已验证</VerifiedBadge>}
                                </Value>
                            </InfoRow>
                            <InfoRow>
                                <Label>个性签名</Label>
                                <Value $italic $dim>
                                    {user.signature || '暂无签名'}
                                </Value>
                            </InfoRow>
                            <InfoRow>
                                <Label>等级</Label>
                                <EditableValue>
                                    <LevelValue>LV.{user.level}<span className="exp">EXP {user.experience}</span></LevelValue>
                                    {user.level === 0 && (
                                        <SmallGhostButton type="button" onClick={() => navigate('/assessment')}>
                                            考核申请
                                        </SmallGhostButton>
                                    )}
                                </EditableValue>
                            </InfoRow>
                            <InfoRow>
                                <Label>注册时间</Label>
                                <Value $mono>{new Date(user.created_at).toLocaleString()}</Value>
                            </InfoRow>
                        </CornerCard>
                    </>
                );

            case 'edit':
                return (
                    <>
                        <ContentHeader>
                            <ContentTitle>資料編集</ContentTitle>
                            <ContentDescription>编辑资料</ContentDescription>
                        </ContentHeader>

                        <CornerCard>
                            {!isEditingProfile ? (
                                <div>
                                    <InfoRow>
                                        <Label>昵称</Label>
                                        <EditableValue>
                                            <Value $dim={!user.nickname}>{user.nickname || '未设置'}</Value>
                                            <SmallGhostButton type="button" onClick={handleEditProfile}>编辑</SmallGhostButton>
                                        </EditableValue>
                                    </InfoRow>
                                    <InfoRow>
                                        <Label>个性签名</Label>
                                        <EditableValue>
                                            <Value $dim={!user.signature}>{user.signature || '暂无签名'}</Value>
                                            <SmallGhostButton type="button" onClick={handleEditProfile}>编辑</SmallGhostButton>
                                        </EditableValue>
                                    </InfoRow>
                                </div>
                            ) : (
                                <Form onSubmit={handleSaveProfile}>
                                    <Field>
                                        <label htmlFor="profile-nickname">昵称</label>
                                        <Input
                                            id="profile-nickname"
                                            type="text"
                                            placeholder="显示名称"
                                            value={editNickname}
                                            onChange={(e) => setEditNickname(e.target.value)}
                                            maxLength={50}
                                        />
                                    </Field>
                                    <Field>
                                        <label htmlFor="profile-signature">个性签名</label>
                                        <TextArea
                                            id="profile-signature"
                                            placeholder="写点什么…"
                                            value={editSignature}
                                            onChange={(e) => setEditSignature(e.target.value)}
                                            maxLength={500}
                                            style={{ minHeight: 120 }}
                                        />
                                    </Field>
                                    <ButtonGroup>
                                        <PrimaryButton type="submit">保存</PrimaryButton>
                                        <GhostButton type="button" onClick={handleCancelEdit}>
                                            取消
                                        </GhostButton>
                                    </ButtonGroup>
                                </Form>
                            )}
                            {profileMessage.text && (
                                <Message $error={profileMessage.error} role={profileMessage.error ? 'alert' : 'status'}>
                                    {profileMessage.text}
                                </Message>
                            )}
                        </CornerCard>
                    </>
                );

            case 'password':
                return (
                    <>
                        <ContentHeader>
                            <ContentTitle>認証</ContentTitle>
                            <ContentDescription>修改密码</ContentDescription>
                        </ContentHeader>

                        <CornerCard>
                            <Form onSubmit={handleChangePassword}>
                                <Field>
                                    <label htmlFor="profile-current-password">当前密码</label>
                                    <Input
                                        id="profile-current-password"
                                        type="password"
                                        placeholder="当前密码"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        required
                                    />
                                </Field>
                                <Field>
                                    <label htmlFor="profile-new-password">新密码</label>
                                    <Input
                                        id="profile-new-password"
                                        type="password"
                                        placeholder="新密码（至少 6 位）"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                </Field>
                                <Field>
                                    <label htmlFor="profile-confirm-password">确认新密码</label>
                                    <Input
                                        id="profile-confirm-password"
                                        type="password"
                                        placeholder="再次输入新密码"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        required
                                    />
                                </Field>
                                <ButtonGroup>
                                    <PrimaryButton type="submit">更新密码</PrimaryButton>
                                </ButtonGroup>
                            </Form>
                            {passwordMessage.text && (
                                <Message $error={passwordMessage.error} role={passwordMessage.error ? 'alert' : 'status'}>
                                    {passwordMessage.text}
                                </Message>
                            )}
                        </CornerCard>
                    </>
                );

            case 'danger':
                return (
                    <>
                        <ContentHeader>
                            <ContentTitle>危険操作</ContentTitle>
                            <ContentDescription>危险操作 · 不可撤销</ContentDescription>
                        </ContentHeader>

                        <DangerZone>
                            <DangerTitle>删除账户</DangerTitle>
                            <DangerDescription>
                                此操作将永久清除数据库中的用户记录，包括：
                                <ul>
                                    <li>个人档案元数据</li>
                                    <li>任务日志与图表</li>
                                    <li>考核成绩与经验值</li>
                                </ul>
                                <strong>此过程不可逆转。</strong>
                            </DangerDescription>

                            {!showDeleteConfirm ? (
                                <DangerGhostButton type="button" onClick={() => setShowDeleteConfirm(true)}>
                                    删除账户
                                </DangerGhostButton>
                            ) : (
                                <Form onSubmit={handleDeleteAccount}>
                                    <Field>
                                        <label htmlFor="profile-delete-password" style={{ color: color.bloodHi }}>确认身份</label>
                                        <Input
                                            id="profile-delete-password"
                                            type="password"
                                            placeholder="输入密码以确认"
                                            value={deletePassword}
                                            onChange={(e) => setDeletePassword(e.target.value)}
                                            required
                                            $invalid
                                        />
                                    </Field>
                                    <ButtonGroup>
                                        <PrimaryButton type="submit">
                                            永久删除
                                        </PrimaryButton>
                                        <GhostButton
                                            type="button"
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeletePassword('');
                                                setDeleteMessage({ text: '', error: false });
                                            }}
                                        >
                                            取消
                                        </GhostButton>
                                    </ButtonGroup>
                                    {deleteMessage.text && (
                                        <Message $error={deleteMessage.error} role={deleteMessage.error ? 'alert' : 'status'}>
                                            {deleteMessage.text}
                                        </Message>
                                    )}
                                </Form>
                            )}
                        </DangerZone>
                    </>
                );

            default:
                return null;
        }
    };

    if (!user) {
        return (
            <PageShell num="伍" title="档案" en="PROFILE">
                <Muted>权限不足 · 请先登录后查看档案</Muted>
            </PageShell>
        );
    }

    return (
        <PageShell num="伍" title="档案" en="PROFILE">
            <ProfileContainer>
                <Sidebar>
                    <SidebarHeader pad="32px 24px">
                        <UserAvatar aria-hidden="true">
                            {user.username.charAt(0).toUpperCase()}
                        </UserAvatar>
                        <Username>{user.username}</Username>
                        <UserRole>{user.role}</UserRole>
                    </SidebarHeader>

                    <NavMenu aria-label="档案分区">
                        {menuItems.map((item) => (
                            <NavItem
                                key={item.id}
                                type="button"
                                $active={activeTab === item.id}
                                $danger={item.danger}
                                aria-current={activeTab === item.id ? 'page' : undefined}
                                onClick={() => setActiveTab(item.id)}
                            >
                                <span className="num">{item.num}</span>
                                {item.label}
                            </NavItem>
                        ))}
                    </NavMenu>
                </Sidebar>

                <MainContent>
                    <ContentWrapper key={activeTab}>
                        {renderContent()}
                    </ContentWrapper>
                </MainContent>
            </ProfileContainer>
        </PageShell>
    );
}

export default Profile;
